import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { setSelectedInstituteId, setSelectedSessionId } from "@store/slices/configSlice";
import { useApplicationSessions } from "@hooks/useApplicationSessions";
import { getDefaultApplicationSession } from "@utils/getDefaultApplicationSession";
import { isFacultyUser, resolveFacultyApiInstituteId } from "@utils/facultyAccess";

/**
 * Loads institute/session context at layout level after login.
 * Faculty: seed numeric `instituteId` from login / JWT
 * so Attendance / Promotion / Dropout / Result use the same institute scope
 * as the institute-owner portal for that campus.
 */
export function useSessionsMenuBootstrap(isAuthenticated: boolean) {
	const dispatch = useDispatch();
	const user = useSelector((state: RootState) => state.user.user);
	const token = useSelector((state: RootState) => state.user.token);
	const selectedInstituteId = useSelector((state: RootState) => state.config.selectedInstituteId);
	const selectedSessionId = useSelector((state: RootState) => state.config.selectedSessionId);

	const isFaculty = isFacultyUser(user, token);
	const facultyInstituteId = resolveFacultyApiInstituteId(user, token);

	const needsDefaultSession =
		isAuthenticated && (selectedSessionId == null || selectedSessionId === "");
	const { data: sessionsData } = useApplicationSessions(
		{ current: true, certified: true, renewal: true },
		{ enabled: needsDefaultSession },
	);

	const activeSessions = useMemo(
		() => sessionsData?.filter(session => session.isActive) ?? [],
		[sessionsData],
	);

	useEffect(() => {
		if (!isAuthenticated || !isFaculty) return;
		if (
			facultyInstituteId &&
			String(selectedInstituteId ?? "") !== String(facultyInstituteId)
		) {
			dispatch(setSelectedInstituteId(facultyInstituteId));
		}
	}, [isAuthenticated, isFaculty, facultyInstituteId, selectedInstituteId, dispatch]);

	useEffect(() => {
		if (!isAuthenticated || activeSessions.length === 0 || selectedSessionId) return;

		const defaultSession = getDefaultApplicationSession(activeSessions);
		if (!defaultSession) return;

		// Seed a registration calendar only when none is selected. Do not override an
		// existing selection — the registration year dropdown manages later changes.
		dispatch(setSelectedSessionId(defaultSession.id.toString()));
	}, [isAuthenticated, selectedSessionId, activeSessions, dispatch]);

}

export default useSessionsMenuBootstrap;
